import { ECONTRACT_STATUS, EPARTY_STATUS } from "../../enums";
import {
  ESenderRole,
  ETrasactionsType,
  IContractDetailsResponseData,
} from "../api/services/contract/models";
import { ROUTES } from "../constants";

/**
 * A two-party transaction is a project with a single milestone in which the organization is the
 * seller (PROVIDER and sender) and one buyer pays. Waffy acts as the broker and holds the money,
 * without being a party on the contract.
 * Both the project and its milestone carry this marker in `metaData`, so the portal can send their
 * drafts back to the two-party wizard and keep multi-party actions (add milestone) off them.
 */
export const TWO_PARTY_META_KEY = "transactionKind";
export const TWO_PARTY_META_VALUE = "TWO_PARTY";

export const isTwoPartyContract = (
  contract?: { metaData?: Record<string, string | number | boolean> } | null
): boolean => contract?.metaData?.[TWO_PARTY_META_KEY] === TWO_PARTY_META_VALUE;

/** `/create-two-party-contract` — also matched by App to hide the sidebar. */
export const TWO_PARTY_CONTRACT_BASE_ROUTE =
  ROUTES.CREATE_TWO_PARTY_CONTRACT.replace("/:complexId?/:milestoneId?", "");

/** The wizard for a new two-party transaction, or for resuming a saved draft. */
export const buildTwoPartyContractRoute = (
  complexId?: string,
  milestoneId?: string
): string =>
  complexId && milestoneId
    ? `${TWO_PARTY_CONTRACT_BASE_ROUTE}/${complexId}/${milestoneId}`
    : TWO_PARTY_CONTRACT_BASE_ROUTE;

const UNPAID_STATUSES: string[] = [
  ECONTRACT_STATUS.CREATED,
  ECONTRACT_STATUS.SUBMITTED,
  ECONTRACT_STATUS.ADDING_PARTY,
];

/**
 * "تعديل" is offered while the buyer has not paid (WAF-3235): the milestone is published but
 * still waiting for its first payment, and the buyer has no payment, pending or done.
 */
export const isTwoPartyEditable = (
  contract?: Pick<
    IContractDetailsResponseData,
    "status" | "parties" | "transactions"
  > | null
): boolean => {
  if (!contract || !UNPAID_STATUSES.includes(contract.status)) return false;
  const buyerHasPaid = contract.parties?.some(
    (party) =>
      party?.role === ESenderRole.CUSTOMER &&
      [EPARTY_STATUS.PAID, EPARTY_STATUS.PAYMENT_PENDING_APPROVAL].includes(
        party?.status as EPARTY_STATUS
      )
  );
  const hasPayment = contract.transactions?.some(
    (transaction) => transaction?.type === ETrasactionsType.CUSTOMER_PAY
  );
  return !buyerHasPaid && !hasPayment;
};
